<template>
  <section class="members-table">
    <gf-entity-members-counter
      :entity-type="entityType"
      :items-count="members?.users?.page?.totalElements || members?.page?.totalElements"
    />

    <gf-entity-members-table
      :entityType="entityType"
      :is-admin="false"
      :users="members?.users || members"
      :teams="members?.teams"
      :base-roles="roles?.baseRoles || roles"
      :company-roles="roles?.companyRoles"
      :global-roles="roles?.globalRoles"
      @role-change="onChange"
      @delete="onDelete"
      @open-ldap-modal="openLdapModal"
      @teams-pagination="onTeamsPagination"
      @users-pagination="onUsersPagination"
    />

    <gf-custom-modal-window
      v-if="ldapInfo?.isLdap"
      class="text-wrap"
      :gf-modal-data-name="`unlockRole-${ldapInfo?.index}`"
      :gf-modal-header="$t('membersWrapper.table.ldap.modalHeader')"
      :gf-close-modal-button-name="`closeUnlockRole-${ldapInfo?.index}`"
      gf-modal-wrapper-width="600px"
      ref="gfCustomLdapModalWindow"
    >
      <template #body>
        <div class="mb-3">
          <div v-if="ldapInfo?.isRoleEqualLdap">
            <b>
              {{ ldapInfo?.fullName }}
            </b>
            @{{ ldapInfo?.username }}
            <br />
            {{ $t('membersWrapper.table.ldap.modalEditDescription') }}
          </div>
          <div v-else>
            {{ $t('membersWrapper.table.ldap.modalLockDescription') }}
            <b>{{ ldapInfo?.fullName }}</b>
            @{{ ldapInfo?.username }}
            {{ $t('membersWrapper.table.ldap.modalCurrenRoleLockDescription') }}
            <br />
            <br />
            {{ $t('membersWrapper.table.ldap.modalResetDescription') }}
          </div>
        </div>
        <div class="d-flex justify-content-end border-top pt-2">
          <button
            type="button"
            :name="`closeUnlockRole-${ldapInfo?.index}`"
            class="btn btn-secondary btn-sm m-1"
          >
            {{ $t('membersWrapper.table.ldap.cancel') }}
          </button>
          <button
            type="button"
            class="btn btn-danger btn-sm m-1"
            @click="sendLdapForm"
          >
            <span>
              {{ modalSubmitButtonText }}
            </span>
          </button>
        </div>
      </template>
    </gf-custom-modal-window>
  </section>
</template>

<script>
import WrapperMessages from '@/shared-modules/shared-components/wrappers/messages/wrapper.messages';
import GfEntityMembersCounter from '@/shared-modules/shared-components/wrappers/wrappers-components/entity-members-table/GfEntityMembersCounter';
import EntityWrapperService from '@/shared-modules/shared-components/wrappers/service/entity.wrapper.service';
import GfEntityMembersTable from '@/shared-modules/shared-components/wrappers/wrappers-components/entity-members-table/GfEntityMembersTable';
import GfCustomModalWindow from '@/shared-modules/shared-components/shared/GfCustomModalWindow';
export default {
  components: { GfEntityMembersTable, GfCustomModalWindow, GfEntityMembersCounter },
  name: 'GfEntityMembersWrapper',
  data() {
    return {
      ldapInfo: null
    };
  },
  props: {
    entityType: {
      type: String,
      default: 'PROJECT'
    },
    entityAlias: {
      type: String,
      default: ''
    },
    userAlias: {
      type: String,
      default: ''
    },
    entityUuid: {
      type: String,
      default: ''
    }
  },
  computed: {
    currentEntityMethods() {
      switch (this.entityType) {
        case 'PROJECT':
          return {
            getMembers: {
              getUsers: this.getProjectMembersUsers,
              getTeams: this.getProjectMembersTeams
            },
            getRoles: this.getProjectRoles,
            deleteUser: this.deleteProjectUser,
            deleteTeam: this.deleteProjectTeam,
            editUserRole: this.editProjectUserRole,
            editTeamRole: this.editProjectTeamRole,
            storeMembersGetterName: 'getProjectMembers'
          };
        case 'TEAM':
          return {
            getMembers: this.getTeamMembers,
            getRoles: this.getTeamRoles,
            deleteUser: this.deleteTeamUser,
            editUserRole: this.editTeamUserRole,
            submitLdapForm: this.submitTeamLdapForm,
            storeMembersGetterName: 'getTeamMembers'
          };
        case 'COMPANY':
          return {
            getMembers: this.getCompanyMembers,
            getRoles: this.getCompanyRoles,
            deleteUser: this.deleteCompanyUser,
            editUserRole: this.editCompanyUserRole,
            submitLdapForm: this.submitCompanyLdapForm,
            storeMembersGetterName: 'getCompanyMembers'
          };
        default:
          return;
      }
    },
    members() {
      return this.$store.getters[`${this.currentEntityMethods.storeMembersGetterName}`];
    },
    roles() {
      return this.$store.getters.getEntityRoles;
    },
    modalSubmitButtonText() {
      return this.ldapInfo.isRoleEqualLdap
        ? this.$t('membersWrapper.table.ldap.unlock')
        : this.$t('membersWrapper.table.ldap.lock');
    },
    currentUserAlias() {
      return this.$store.getters.getUserAlias;
    }
  },
  mounted() {
    this.getTableContent();
  },
  methods: {
    // Project
    getProjectMembersUsers(params) {
      EntityWrapperService.getProjectMembersUsers(this.userAlias, this.entityAlias, params);
    },
    getProjectMembersTeams(params) {
      EntityWrapperService.getProjectMembersTeams(this.userAlias, this.entityAlias, params);
    },
    getProjectRoles() {
      EntityWrapperService.getProjectRoles(this.userAlias, this.entityAlias);
    },
    deleteProjectUser(userUuid, userToDeleteAlias) {
      EntityWrapperService.deleteProjectUser(this.userAlias, this.entityAlias, userUuid, {
        currentUser: this.currentUserAlias,
        userToDeleteAlias: userToDeleteAlias
      }).then(() => {
        this.getProjectMembersUsers();
      });
    },
    deleteProjectTeam(teamUuid) {
      EntityWrapperService.deleteProjectTeam(this.userAlias, this.entityAlias, teamUuid).then(
        () => {
          this.getProjectMembersTeams();
        }
      );
    },
    editProjectUserRole(data) {
      EntityWrapperService.editProjectUserRole(this.userAlias, this.entityAlias, {
        userId: data.itemUuid,
        roleIdentity: data.role || data.name
      }).then(() => {
        this.getProjectMembersUsers();
      });
    },
    editProjectTeamRole(data) {
      EntityWrapperService.editProjectTeamRole(this.userAlias, this.entityAlias, {
        teamUuid: data.itemUuid,
        projectUuid: this.entityUuid,
        role: data.role || data.name
      }).then(() => {
        this.getProjectMembersTeams();
      });
    },

    // Team
    getTeamMembers(params) {
      EntityWrapperService.getTeamMembers(this.entityAlias, params);
    },
    getTeamRoles() {
      EntityWrapperService.getTeamRoles(this.entityAlias);
    },
    deleteTeamUser(userUuid, userToDeleteAlias) {
      EntityWrapperService.deleteTeamUser(this.entityAlias, userUuid, {
        currentUser: this.currentUserAlias,
        userToDeleteAlias: userToDeleteAlias
      }).then(() => {
        this.getTeamMembers();
      });
    },
    editTeamUserRole(data) {
      EntityWrapperService.editTeamUserRole(this.entityAlias, {
        userUuid: data.itemUuid,
        teamUuid: this.entityUuid,
        role: data.role
      }).then(() => {
        this.getTeamMembers();
      });
    },
    submitTeamLdapForm(data) {
      EntityWrapperService.ldapTeamRemoveUser(this.entityAlias, data.username, data.action).then(() => {
        this.closeLdapModal();
        this.getTeamMembers();
      });
    },

    // Company
    getCompanyMembers(params) {
      EntityWrapperService.getCompanyMembers(this.entityAlias, params);
    },
    getCompanyRoles() {
      EntityWrapperService.getCompanyRoles(this.entityAlias);
    },
    deleteCompanyUser(userUuid, userToDeleteAlias) {
      EntityWrapperService.deleteCompanyUser(this.entityAlias, userUuid, {
        currentUser: this.currentUserAlias,
        userToDeleteAlias: userToDeleteAlias
      }).then(() => {
        this.getCompanyMembers();
      });
    },
    editCompanyUserRole(data) {
      EntityWrapperService.editCompanyUserRole(this.entityAlias, {
        userUuid: data.itemUuid,
        companyUuid: this.entityUuid,
        role: data.role
      }).then(() => {
        this.getCompanyMembers();
      });
    },
    submitCompanyLdapForm(data) {
      EntityWrapperService.ldapCompanyRemoveUser(this.entityAlias, data.username, data.action).then(() => {
        this.closeLdapModal();
        this.getCompanyMembers();
      });
    },

    onChange(data) {
      data.type === 'USER'
        ? this.currentEntityMethods.editUserRole(data)
        : this.currentEntityMethods.editTeamRole(data);
    },
    onDelete(data) {
      data.type === 'USER'
        ? this.currentEntityMethods.deleteUser(data.uuid, data.alias)
        : this.currentEntityMethods.deleteTeam(data.uuid);
    },
    openLdapModal(data) {
      this.ldapInfo = data;
      this.$nextTick(() => {
        this.$refs.gfCustomLdapModalWindow.openModalWindow();
      });
    },
    closeLdapModal() {
      this.ldapInfo = {};
      this.$refs.gfCustomLdapModalWindow.closeModalWindow();
    },
    sendLdapForm() {
      this.currentEntityMethods.submitLdapForm(this.ldapInfo);
    },
    onTeamsPagination(page) {
      this.$router
        .push({
          query: { ...this.$route.query, teamsPage: page }
        })
        .then(() => {
          this.getTableContent();
        });
    },
    onUsersPagination(page) {
      this.$router
        .push({
          query: { ...this.$route.query, usersPage: page }
        })
        .then(() => {
          this.getTableContent();
        });
    },
    getTableContent() {
      if (this.entityType === 'PROJECT') {
        this.currentEntityMethods.getMembers.getUsers({ page: this.$route.query.usersPage });
        this.currentEntityMethods.getMembers.getTeams({ page: this.$route.query.teamsPage });
        this.currentEntityMethods.getRoles();
      } else {
        this.currentEntityMethods.getMembers({ page: this.$route.query.usersPage });
        this.currentEntityMethods.getRoles();
      }
    }
  },
  i18n: {
    messages: WrapperMessages
  }
};
</script>
